"use client"

import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react"
import type { DeliverableDraft, DeliverableDraftUpdate } from "@/schema/deliverable"
import { Skeleton } from "@ui/shad"

import { useDeliverableDraft } from "@/hooks/api/deliverables"

import {
  DOCUMENT_EDIT_RUNTIME_SCRIPT,
  DOCUMENT_EDIT_RUNTIME_STYLES,
  EDITOR_CONFIG_ELEMENT_ID,
  EDITOR_MESSAGE_TYPES,
  type DocumentEditRuntimeConfig,
} from "./document-edit-runtime"

// Same srcdoc kick as SandboxedDocument: Chromium sometimes never navigates a
// freshly assigned sandboxed srcdoc frame. Each assignment carries a load id
// so a ready message from an earlier load cannot stop the kicking while a
// later assignment is the one stuck; kicking ends only once the latest load
// confirms, and never restarts, since a re-assignment would drop edits.
const KICK_INTERVAL_MS = 1500
const MAX_KICKS = 10
const HTML_REQUEST_TIMEOUT_MS = 5_000

export interface DocumentEditHistory {
  canUndo: boolean
  canRedo: boolean
}

export interface DocumentEditFrameHandle {
  requestDraft: () => Promise<DeliverableDraftUpdate>
  /** Records the revision this client just saved so the refetched draft is adopted without a reload. */
  adoptRevision: (revision: number) => void
  undo: () => void
  redo: () => void
}

interface DocumentEditFrameProps {
  deliverableId: string
  /** Turned off while the chat is rewriting the document. */
  editable?: boolean
  onDirtyChange?: (dirty: boolean) => void
  onHistoryChange?: (history: DocumentEditHistory) => void
  /** Focus left the document, e.g. the advisor clicked into the chat. */
  onBlur?: () => void
  className?: string
}

interface LoadedDraft {
  key: number
  draft: DeliverableDraft
}

export const DocumentEditFrame = forwardRef<DocumentEditFrameHandle, DocumentEditFrameProps>(function DocumentEditFrame(
  { deliverableId, editable = true, onDirtyChange, onHistoryChange, onBlur, className },
  ref
) {
  const { data: draft } = useDeliverableDraft(deliverableId)
  const frameRef = useRef<EditableDocumentHandle>(null)
  const [loaded, setLoaded] = useState<LoadedDraft | null>(null)
  const revisionRef = useRef<number | null>(null)

  useEffect(() => {
    if (!draft) return
    if (draft.editor_revision === revisionRef.current) {
      setLoaded((current) => (current && current.draft !== draft ? { ...current, draft } : current))
      return
    }

    revisionRef.current = draft.editor_revision
    setLoaded((current) => ({ key: current ? current.key + 1 : 1, draft }))
  }, [draft])

  useImperativeHandle(
    ref,
    () => ({
      requestDraft: async () => {
        if (revisionRef.current === null || !frameRef.current) throw new Error("The document editor is not ready")
        return { document_html: await frameRef.current.requestHtml(), editor_revision: revisionRef.current }
      },
      adoptRevision: (revision: number) => {
        revisionRef.current = revision
      },
      undo: () => frameRef.current?.send({ type: EDITOR_MESSAGE_TYPES.undo }),
      redo: () => frameRef.current?.send({ type: EDITOR_MESSAGE_TYPES.redo }),
    }),
    []
  )

  if (!loaded) return <Skeleton className={className} />

  return (
    <EditableDocument
      key={loaded.key}
      ref={frameRef}
      draft={loaded.draft}
      editable={editable}
      onDirtyChange={onDirtyChange}
      onHistoryChange={onHistoryChange}
      onBlur={onBlur}
      className={className}
    />
  )
})

interface EditableDocumentHandle {
  requestHtml: () => Promise<string>
  send: (message: Record<string, unknown>) => void
}

interface EditableDocumentProps {
  draft: DeliverableDraft
  editable: boolean
  onDirtyChange?: (dirty: boolean) => void
  onHistoryChange?: (history: DocumentEditHistory) => void
  onBlur?: () => void
  className?: string
}

function buildEditableDocument(draft: DeliverableDraft, loadId: number) {
  const config: DocumentEditRuntimeConfig = { attachments: draft.assets.attachments, loadId }
  const extras = [
    `<style data-slant-editor="assets">${draft.assets.font_css}</style>`,
    `<style data-slant-editor="styles">${DOCUMENT_EDIT_RUNTIME_STYLES}</style>`,
    `<script type="application/json" id="${EDITOR_CONFIG_ELEMENT_ID}" data-slant-editor="config">${JSON.stringify(config).replace(/</g, "\\u003c")}</script>`,
    `<script data-slant-editor="paginator">${draft.assets.paginator_js}</script>`,
    `<script data-slant-editor="runtime">${DOCUMENT_EDIT_RUNTIME_SCRIPT}</script>`,
  ].join("\n")
  const html = draft.document_html
  return /<\/body>/i.test(html) ? html.replace(/<\/body>/i, `${extras}</body>`) : `${html}${extras}`
}

function messageType(data: unknown): string | null {
  return typeof data === "object" && data !== null && "type" in data && typeof data.type === "string" ? data.type : null
}

const EditableDocument = forwardRef<EditableDocumentHandle, EditableDocumentProps>(function EditableDocument(
  { draft, editable, onDirtyChange, onHistoryChange, onBlur, className },
  ref
) {
  const frameRef = useRef<HTMLIFrameElement>(null)
  const pendingRequests = useRef(new Map<string, (html: string) => void>())
  const latestCallbacks = useRef({ onDirtyChange, onHistoryChange, onBlur })
  latestCallbacks.current = { onDirtyChange, onHistoryChange, onBlur }
  const editableRef = useRef(editable)
  editableRef.current = editable
  // Built once per mount: a newer revision arrives through a new key, and this
  // client's own saved revision must not reassign srcdoc or edits would be lost.
  const [initial] = useState(() => ({ draft }))

  const send = (message: Record<string, unknown>) => frameRef.current?.contentWindow?.postMessage(message, "*")

  useEffect(() => {
    const frame = frameRef.current
    if (!frame) return

    let assignment = 0
    let readyAssignment = 0

    function assign() {
      assignment += 1
      frame?.setAttribute("srcdoc", buildEditableDocument(initial.draft, assignment))
    }

    function onMessage(event: MessageEvent) {
      if (event.source !== frame?.contentWindow) return
      const data: unknown = event.data
      switch (messageType(data)) {
        case EDITOR_MESSAGE_TYPES.ready: {
          const { loadId } = data as { loadId?: number }
          if (typeof loadId === "number") readyAssignment = loadId
          break
        }
        case EDITOR_MESSAGE_TYPES.loaded:
          frame?.contentWindow?.postMessage(
            { type: EDITOR_MESSAGE_TYPES.setEditable, editable: editableRef.current },
            "*"
          )
          break
        case EDITOR_MESSAGE_TYPES.dirty:
          latestCallbacks.current.onDirtyChange?.(true)
          break
        case EDITOR_MESSAGE_TYPES.blur:
          latestCallbacks.current.onBlur?.()
          break
        case EDITOR_MESSAGE_TYPES.history: {
          const { canUndo, canRedo } = data as DocumentEditHistory
          latestCallbacks.current.onHistoryChange?.({ canUndo, canRedo })
          break
        }
        case EDITOR_MESSAGE_TYPES.html: {
          const { requestId, html } = data as { requestId: string; html: string }
          pendingRequests.current.get(requestId)?.(html)
          pendingRequests.current.delete(requestId)
          break
        }
      }
    }

    window.addEventListener("message", onMessage)
    assign()
    const kicker = setInterval(() => {
      if (readyAssignment === assignment || assignment > MAX_KICKS) {
        clearInterval(kicker)
        return
      }
      assign()
    }, KICK_INTERVAL_MS)

    return () => {
      clearInterval(kicker)
      window.removeEventListener("message", onMessage)
    }
  }, [initial])

  useEffect(() => {
    send({ type: EDITOR_MESSAGE_TYPES.setEditable, editable })
  }, [editable])

  useImperativeHandle(
    ref,
    () => ({
      send,
      requestHtml: () =>
        new Promise<string>((resolve, reject) => {
          const target = frameRef.current?.contentWindow
          if (!target) {
            reject(new Error("The document editor is not ready"))
            return
          }

          const requestId = crypto.randomUUID()
          const timeout = window.setTimeout(() => {
            pendingRequests.current.delete(requestId)
            reject(new Error("The document editor did not respond"))
          }, HTML_REQUEST_TIMEOUT_MS)
          pendingRequests.current.set(requestId, (html) => {
            window.clearTimeout(timeout)
            resolve(html)
          })
          target.postMessage({ type: EDITOR_MESSAGE_TYPES.requestHtml, requestId }, "*")
        }),
    }),
    []
  )

  // Popups are allowed so a click on a document link can open a new tab;
  // the opened page escapes the sandbox so it behaves as a normal site.
  return (
    <iframe
      ref={frameRef}
      sandbox="allow-scripts allow-popups allow-popups-to-escape-sandbox"
      title="Edit document"
      className={className}
    />
  )
})
